import React, { useMemo } from 'react';
import { cn } from '../../lib/classNameHelpers';

type AnimationAction = {
  type: 'draw' | 'undraw';
  startTime: number;
  endTime: number;
};

export type LineAnimation = {
  id: string;
  length: number;
  actions: AnimationAction[];
  startsDrawn?: boolean;
};

const timeToPercent = (timestamp: number, animationDuration: number): string =>
  ((timestamp / animationDuration) * 100).toFixed(2);

// Generate the CSS styles for a line's animation.
const generateLineAnimationStyle = (
  animation: LineAnimation,
  animationClass: string,
  animationDuration: number
) => {
  const { id, length, actions, startsDrawn } = animation;

  // Build keyframe points
  const keyframes: Array<{ percent: string; dasharray: string; offset: number }> = [];

  // Initial state
  keyframes.push({
    percent: '0%',
    dasharray: `${length} ${length * 2}`,
    offset: startsDrawn ? 0 : length,
  });

  let currentOffset = startsDrawn ? 0 : length;

  actions.forEach((action) => {
    const startPercent = timeToPercent(action.startTime, animationDuration);
    const endPercent = timeToPercent(action.endTime, animationDuration);

    // Add keyframe at action start (maintain current state)
    keyframes.push({
      percent: `${startPercent}%`,
      dasharray: `${length} ${length * 2}`,
      offset: currentOffset,
    });

    // Calculate new offset after action
    if (action.type === 'draw') {
      currentOffset = 0;
    } else if (action.type === 'undraw') {
      currentOffset = length;
    }

    // Add keyframe at action end
    keyframes.push({
      percent: `${endPercent}%`,
      dasharray: `${length} ${length * 2}`,
      offset: currentOffset,
    });
  });

  // Final state
  keyframes.push({
    percent: '100%',
    dasharray: `${length} ${length * 2}`,
    offset: currentOffset,
  });

  return `
.${animationClass} .${id} {
  stroke-dasharray: ${length} ${length * 2};
  stroke-dashoffset: ${startsDrawn ? 0 : length};
  animation: ${id}Animation ${animationDuration}s linear infinite;
}

@keyframes ${id}Animation {
  ${keyframes
    .map(
      (kv) => `${kv.percent} { stroke-dasharray: ${kv.dasharray}; stroke-dashoffset: ${kv.offset};}`
    )
    .join('\n  ')}
}`;
};

type AnimatedSvgProps = {
  children: React.ReactNode;
  animations: readonly LineAnimation[];
  animationDuration: number;
  animationClass: string;
  className?: string;
  autoPlay?: boolean;
};

/**
 * AnimatedSvg supports simple animated SVGs with lines being drawn and un-drawn.
 * Animations are specified by providing an SVG and an array of `LineAnimation`s.
 */
export const AnimatedSvg: React.FC<AnimatedSvgProps> = ({
  children,
  animations,
  animationDuration,
  animationClass,
  className,
  autoPlay = true,
}) => {
  const styleContent = useMemo(
    () =>
      animations
        .map((animation) =>
          generateLineAnimationStyle(animation, animationClass, animationDuration)
        )
        .join(''),
    [animations, animationClass, animationDuration]
  );

  return (
    <div className={cn(className, autoPlay ? animationClass : '')}>
      <style>{styleContent}</style>
      {children}
    </div>
  );
};
